import { useContext, useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Input from "../ui/Input";
import { tl } from "../../utils/format";
import { useHistory, useParams } from "react-router-dom";
import AuthContext from "../../Context/AuthContext";
import { normalizeCoordinateString } from "../../utils/coordinates";
import authHeader from "../../utils/authHeader";

const PLAN_TYPES = ["Dijital", "Poster"];
const USAGE_TYPES = ["Birim", "Paket", "Free"];
const USAGE_AREAS = ["Medya", "Operasyon", "Baskı"];
const UNIT_TYPES = ["CLP", "Billboard", "Giantboard", "Megalight", "Megaboard", "Duvar"];
const CUSTOM_UNIT_TYPE_VALUE = "__custom_unit_type__";
const TURKEY_CITIES = [
  "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Amasya", "Ankara", "Antalya", "Artvin", "Aydın", "Balıkesir",
  "Bilecik", "Bingöl", "Bitlis", "Bolu", "Burdur", "Bursa", "Çanakkale", "Çankırı", "Çorum", "Denizli",
  "Diyarbakır", "Edirne", "Elazığ", "Erzincan", "Erzurum", "Eskişehir", "Gaziantep", "Giresun", "Gümüşhane", "Hakkari",
  "Hatay", "Isparta", "Mersin", "İstanbul", "İzmir", "Kars", "Kastamonu", "Kayseri", "Kırklareli", "Kırşehir",
  "Kocaeli", "Konya", "Kütahya", "Malatya", "Manisa", "Kahramanmaraş", "Mardin", "Muğla", "Muş", "Nevşehir",
  "Niğde", "Ordu", "Rize", "Sakarya", "Samsun", "Siirt", "Sinop", "Sivas", "Tekirdağ", "Tokat",
  "Trabzon", "Tunceli", "Şanlıurfa", "Uşak", "Van", "Yozgat", "Zonguldak", "Aksaray", "Bayburt", "Karaman",
  "Kırıkkale", "Batman", "Şırnak", "Bartın", "Ardahan", "Iğdır", "Yalova", "Karabük", "Kilis", "Osmaniye", "Düzce",
];
const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const makeCampaignLine = () => ({
  id: makeId(), planType: "", usageType: "", usageArea: "", unitType: "", city: "", startDate: "", endDate: "", period: "",
  unitNumber: "", faces: "", unitPrice: "", frequency: "", packagePrice: "", fileName: "", locations: [], isCustomUnitType: false,
});

const usesFrequency = (line) => line.planType === "Dijital" && line.usageType === "Birim";
const usesUsageArea = (line) => line.planType === "Poster" && line.usageType === "Birim";
const usesUnitOnlyPricing = (line) => usesUsageArea(line) && ["Operasyon", "Baskı"].includes(line.usageArea);

const lineBudget = (line) => {
  if (line.usageType === "Free") return 0;
  if (line.usageType === "Paket") return Number(line.packagePrice) || 0;
  if (usesUnitOnlyPricing(line)) return (Number(line.unitPrice) || 0) * (Number(line.unitNumber) || 0);
  const base = (Number(line.unitPrice) || 0) * (Number(line.period) || 0) * (Number(line.unitNumber) || 0);
  return usesFrequency(line) ? base * (Number(line.frequency) || 0) : base;
};

const hasValidDates = (line) =>
  line.startDate && line.endDate && new Date(line.endDate) >= new Date(line.startDate);

const isPositiveInteger = (value) => Number.isInteger(Number(value)) && Number(value) >= 1;

const hasValidPricing = (line) => {
  if (!USAGE_TYPES.includes(line.usageType)) return false;
  if (usesUsageArea(line) && !USAGE_AREAS.includes(line.usageArea)) return false;
  if (line.usageType === "Free") return true;
  if (line.usageType === "Paket") return line.packagePrice !== "" && Number(line.packagePrice) >= 0;
  return line.unitPrice !== "" && Number(line.unitPrice) >= 0 && (!usesFrequency(line) || isPositiveInteger(line.frequency));
};

const isLineComplete = (line) => Boolean(
  PLAN_TYPES.includes(line.planType) && USAGE_TYPES.includes(line.usageType) && line.unitType.trim() && TURKEY_CITIES.includes(line.city) && hasValidDates(line) &&
  isPositiveInteger(line.period) && isPositiveInteger(line.unitNumber) && isPositiveInteger(line.faces) &&
  hasValidPricing(line)
);

const excelValue = (row, keys) => {
  const entries = Object.entries(row || {});
  return entries.find(([key]) => keys.some((candidate) =>
    key.trim().toLocaleLowerCase("tr-TR") === candidate.toLocaleLowerCase("tr-TR")
  ))?.[1];
};

const COORDINATE_KEYS = ["Koordinat", "Koordinatlar", "Kordinat", "Coordinate", "Coordinates", "LatLng", "Lat Long", "Lat/Lng"];

const parseCoordinatePair = (value) => {
  const text = String(value || "").trim();
  if (!text) return { latitude: "", longitude: "" };
  const parts = text.match(/-?\d+(?:[.,]\d+)?/g) || [];
  if (parts.length < 2) return { latitude: "", longitude: "" };
  return {
    latitude: normalizeCoordinateString(parts[0], "lat"),
    longitude: normalizeCoordinateString(parts[1], "lng"),
  };
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .replaceAll("İ", "i");

const normalizeUnitType = (value) => {
  const text = String(value || "").trim();
  return normalizeText(text) === normalizeText("Giandtboard") ? "Giantboard" : text;
};

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const stringValue = (value) =>
  value === null || value === undefined || value === "" ? "" : String(value);

export default function LCWCampaignPlanForm({
  onSaved,
  saveUrl = "https://opt-api.vercel.app/NewPlan",
}) {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [name, setName] = useState("");
  const [campaignLines, setCampaignLines] = useState(() => [makeCampaignLine()]);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successPopup, setSuccessPopup] = useState(false);
  const history = useHistory();
  const { userData } = useContext(AuthContext);
  const apiBase = saveUrl.replace(/\/NewPlan$/, "");

  useEffect(() => {
    if (!isEditMode) return;
    let alive = true;
    (async () => {
      try {
        setLoadingPlan(true);
        const { data } = await axios.get(`${apiBase}/Plans/${id}`, authHeader());
        if (!alive) return;
        setName(data.name || "");
        const lines = Array.isArray(data.campaignLines) && data.campaignLines.length
          ? data.campaignLines.map((line) => {
            const normalizedUnitType = normalizeUnitType(line.unitType);
            return ({
              id: line._id || makeId(),
              planType: line.planType || "",
              usageType: line.usageType || "",
              usageArea: line.usageArea || (line.planType === "Poster" && line.usageType === "Birim" ? "Medya" : ""),
              unitType: normalizedUnitType,
              isCustomUnitType: Boolean(normalizedUnitType && !UNIT_TYPES.includes(normalizedUnitType)),
              city: line.city || "",
              startDate: toDateInput(line.startDate),
              endDate: toDateInput(line.endDate),
              period: stringValue(line.period),
              unitNumber: stringValue(line.unitNumber),
              faces: stringValue(line.faces),
              unitPrice: stringValue(line.unitPrice),
              frequency: stringValue(line.frequency || 1),
              packagePrice: stringValue(line.packagePrice),
              fileName: line.locations?.length ? "Kayıtlı lokasyonlar" : "",
              locations: (line.locations || []).map((location) => ({
                id: location._id || makeId(),
                unitType: normalizeUnitType(location.unitType || line.unitType || ""),
                code: location.code || "",
                address: location.address || "",
                latitude: location.latitude || "",
                longitude: location.longitude || "",
                quantity: Number(location.quantity) || 0,
              })),
            });
          })
          : [makeCampaignLine()];
        setCampaignLines(lines);
      } catch (error) {
        alert(`Plan düzenleme için alınamadı: ${error.response?.data?.hata || error.message || String(error)}`);
        history.push("/homepage");
      } finally {
        if (alive) setLoadingPlan(false);
      }
    })();
    return () => { alive = false; };
  }, [apiBase, history, id, isEditMode]);

  const updateLine = (id, patch) => setCampaignLines((lines) =>
    lines.map((line) => line.id === id ? { ...line, ...patch } : line)
  );
  const removeLine = (id) => setCampaignLines((lines) => lines.filter((line) => line.id !== id));
  const removeLocation = (lineId, locationId) => setCampaignLines((lines) => lines.map((line) =>
    line.id === lineId
      ? { ...line, locations: line.locations.filter((location) => location.id !== locationId) }
      : line
  ));

  const handleFileUpload = (lineId, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      try {
        const workbook = XLSX.read(loadEvent.target.result, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const targetLine = campaignLines.find((line) => line.id === lineId);
        const targetUnitType = targetLine?.unitType || "";
        const normalizedTargetUnitType = normalizeText(targetUnitType);
        const parsedRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        const rowsWithUnitType = parsedRows.filter((row) => String(excelValue(row, ["Ünite Tipi", "Unite Tipi", "Ünite", "Unite"]) || "").trim());
        const relevantRows = rowsWithUnitType.length
          ? rowsWithUnitType.filter((row) => normalizeText(excelValue(row, ["Ünite Tipi", "Unite Tipi", "Ünite", "Unite"])) === normalizedTargetUnitType)
          : parsedRows;
        if (rowsWithUnitType.length && !relevantRows.length) {
          alert(`Excel içinde ${targetUnitType} ünite tipine ait lokasyon bulunamadı.`);
          return;
        }
        const locations = relevantRows.map((row) => {
          const coordinatePair = parseCoordinatePair(excelValue(row, COORDINATE_KEYS));
          const latitude = normalizeCoordinateString(excelValue(row, ["Latitude", "Enlem"]), "lat") || coordinatePair.latitude;
          const longitude = normalizeCoordinateString(excelValue(row, ["Longtitude", "Longitude", "Boylam"]), "lng") || coordinatePair.longitude;
          return {
            id: makeId(),
            unitType: normalizeUnitType(excelValue(row, ["Ünite Tipi", "Unite Tipi", "Ünite", "Unite"]) || targetUnitType),
            code: String(excelValue(row, ["Kod", "KOD"]) || "").trim(),
            address: String(excelValue(row, ["Adres", "ADRES"]) || "").trim(),
            latitude,
            longitude,
            quantity: Number(excelValue(row, ["Adet"])) || 0,
          };
        }).filter((location) => location.address);
        if (!locations.length) {
          alert("Excel dosyasında Ünite Tipi, Adres, Latitude/Longtitude veya Koordinat, Adet formatında geçerli bir lokasyon bulunamadı.");
          return;
        }
        updateLine(lineId, { locations, fileName: file.name });
      } catch (error) {
        alert(`Excel dosyası okunamadı: ${error.message}`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const totalBudget = campaignLines.reduce((sum, line) => sum + lineBudget(line), 0);
  const canAddLine = campaignLines.every((line) => isLineComplete(line));

  const validate = () => {
    const errors = [];
    if (!name.trim()) errors.push("Kampanya ismi gerekli.");
    if (!campaignLines.length) errors.push("En az bir ünite tipi eklenmeli.");
    campaignLines.forEach((line, index) => {
      const prefix = `${index + 1}. ünite satırı`;
      if (!PLAN_TYPES.includes(line.planType)) errors.push(`${prefix}: Plan tipi seçilmeli.`);
      if (!USAGE_TYPES.includes(line.usageType)) errors.push(`${prefix}: Kullanım tipi seçilmeli.`);
      if (usesUsageArea(line) && !USAGE_AREAS.includes(line.usageArea)) errors.push(`${prefix}: Kullanım alanı seçilmeli.`);
      if (!line.unitType.trim()) errors.push(`${prefix}: Ünite tipi seçilmeli.`);
      if (!TURKEY_CITIES.includes(line.city)) errors.push(`${prefix}: Şehir listeden seçilmeli.`);
      if (!line.startDate) errors.push(`${prefix}: Başlangıç tarihi gerekli.`);
      if (!line.endDate) errors.push(`${prefix}: Bitiş tarihi gerekli.`);
      if (line.startDate && line.endDate && !hasValidDates(line)) errors.push(`${prefix}: Bitiş tarihi başlangıç tarihinden önce olamaz.`);
      if (!isPositiveInteger(line.period)) errors.push(`${prefix}: Periyot pozitif tam sayı olmalı.`);
      if (!isPositiveInteger(line.unitNumber)) errors.push(`${prefix}: Ünite sayısı pozitif tam sayı olmalı.`);
      if (!isPositiveInteger(line.faces)) errors.push(`${prefix}: Face sayısı pozitif tam sayı olmalı.`);
      if (line.usageType === "Birim") {
        if (line.unitPrice === "" || Number(line.unitPrice) < 0) errors.push(`${prefix}: Geçerli bir ünite fiyatı girilmeli.`);
        if (usesFrequency(line) && !isPositiveInteger(line.frequency)) errors.push(`${prefix}: Frekans pozitif tam sayı olmalı.`);
      }
      if (line.usageType === "Paket" && (line.packagePrice === "" || Number(line.packagePrice) < 0)) errors.push(`${prefix}: Geçerli bir paket fiyatı girilmeli.`);
    });
    return errors;
  };

  const handleSave = async () => {
    const errors = validate();
    if (errors.length) {
      alert(`Lütfen hataları düzeltin:\n\n${errors.join("\n")}`);
      return;
    }
    const payload = {
      name: name.trim(),
      brandId: userData?.user?.user?.username || "",
      currency: "TRY",
      notes: "",
      campaignLines: campaignLines.map((line) => ({
        planType: line.planType,
        usageType: line.usageType,
        usageArea: usesUsageArea(line) ? line.usageArea : undefined,
        unitType: normalizeUnitType(line.unitType),
        city: line.city.trim(),
        startDate: new Date(line.startDate),
        endDate: new Date(line.endDate),
        period: Number(line.period),
        unitNumber: Number(line.unitNumber),
        faces: Number(line.faces),
        unitPrice: line.usageType === "Birim" ? Number(line.unitPrice) : 0,
        frequency: usesFrequency(line) ? Number(line.frequency) : 1,
        packagePrice: line.usageType === "Paket" ? Number(line.packagePrice) : 0,
        locations: line.locations.map((location) => ({
          unitType: normalizeUnitType(location.unitType || line.unitType),
          code: location.code, address: location.address,
          latitude: normalizeCoordinateString(location.latitude, "lat") || undefined,
          longitude: normalizeCoordinateString(location.longitude, "lng") || undefined,
          quantity: Number(location.quantity) || 0,
        })),
      })),
    };
    setSaving(true);
    try {
      const response = isEditMode
        ? await axios.put(`${apiBase}/Plans/${id}`, payload, authHeader())
        : await axios.post(saveUrl, payload, authHeader());
      onSaved?.(response.data);
      setSuccessPopup(true);
      setTimeout(() => history.push(isEditMode ? `/CampaignDetail/${id}` : "/homepage"), 1200);
    } catch (error) {
      alert(`Kaydetme sırasında hata: ${error.response?.data?.hata || error.message || String(error)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {loadingPlan && <div className="text-slate-600">Plan yükleniyor…</div>}
      <div className="flex items-center gap-3 bg-white p-4 rounded shadow-sm">
        <button type="button" onClick={() => history.push(isEditMode ? `/CampaignDetail/${id}` : "/homepage")} className="px-3 py-1 rounded border text-sm">‹ Geri</button>
        <div className="ms-auto flex items-center gap-3">
          <div className="text-sm text-slate-600">Toplam Bütçe: <span className="font-semibold">{tl(totalBudget)}</span></div>
          <button type="button" onClick={handleSave} disabled={saving} className="h-10 px-4 rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
            {saving ? "Kaydediliyor..." : isEditMode ? "Değişiklikleri Kaydet" : "Kampanyayı Kaydet"}
          </button>
        </div>
      </div>

      <div className="card p-4 bg-white rounded shadow-sm">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kampanya İsmi"><Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Örn: İstanbul Yaz Outdoor" /></Field>
          {/* <div className="flex items-end text-sm text-slate-500">Plan tipi her ünite satırı için ayrı seçilir.</div> */}
        </div>
      </div>

      {campaignLines.map((line, index) => (
        <CampaignLineCard key={line.id} line={line} index={index} removable={campaignLines.length > 1}
          updateLine={updateLine} removeLine={removeLine} removeLocation={removeLocation}
          handleFileUpload={handleFileUpload} />
      ))}

      <button type="button" onClick={() => setCampaignLines((lines) => [...lines, makeCampaignLine()])}
          disabled={!canAddLine}
          className="w-full h-11 rounded border-2 border-dashed border-slate-300 text-slate-600 hover:border-brand-400 hover:text-brand-700 disabled:opacity-40 disabled:cursor-not-allowed">
          + Farklı Ünite Tipi Ekle
      </button>

      {successPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 w-[360px] text-center shadow-2xl">
            <h2 className="text-xl font-semibold text-emerald-600">Plan Başarıyla Kaydedildi</h2>
            <p className="text-slate-500 text-sm mt-2">Yönlendiriliyorsunuz...</p>
          </div>
        </div>
      )}
    </div>
  );
}

function CampaignLineCard({ line, index, removable, updateLine, removeLine, removeLocation, handleFileUpload }) {
  const usageSelected = line.planType && line.usageType;
  const datesVisible = usageSelected && line.unitType.trim() && line.city.trim();
  const pricingVisible = datesVisible && line.startDate && line.endDate;
  const uploadEnabled = isLineComplete(line);
  const unitTypeSelectValue = line.isCustomUnitType ? CUSTOM_UNIT_TYPE_VALUE : line.unitType;
  return (
    <div className="card bg-white rounded shadow-sm overflow-hidden">
      <div className="p-4 border-b flex items-center justify-between bg-slate-50">
        <div><span className="font-semibold">Ünite Planı {index + 1}</span>{line.planType && <span className="ml-2 text-sm text-slate-500">{line.planType}{line.usageType ? ` / ${line.usageType}` : ""}{line.usageArea ? ` / ${line.usageArea}` : ""}{line.unitType ? ` / ${line.unitType}` : ""}</span>}</div>
        {removable && <button type="button" onClick={() => removeLine(line.id)} className="text-sm text-rose-600">Üniteyi Sil</button>}
      </div>
      <div className="p-4 space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <Field label="Plan Tipi"><Select value={line.planType} onChange={(event) => updateLine(line.id, { planType: event.target.value, usageType: "", usageArea: "", unitPrice: "", frequency: "", packagePrice: "" })} placeholder="Plan tipi seçin" options={PLAN_TYPES} /></Field>
          {line.planType && <Field label="Kullanım Tipi"><Select value={line.usageType} onChange={(event) => updateLine(line.id, { usageType: event.target.value, usageArea: event.target.value === "Birim" && line.planType === "Poster" ? "Medya" : "", unitPrice: "", frequency: "", packagePrice: "" })} placeholder="Kullanım tipi seçin" options={USAGE_TYPES} /></Field>}
          {usesUsageArea(line) && <Field label="Kullanım Alanı"><Select value={line.usageArea} onChange={(event) => updateLine(line.id, { usageArea: event.target.value })} placeholder="Kullanım alanı seçin" options={USAGE_AREAS} /></Field>}
          {usageSelected && (
            <Field label="Ünite Tipi">
              <Select
                value={unitTypeSelectValue}
                onChange={(event) => {
                  const value = event.target.value;
                  updateLine(line.id, value === CUSTOM_UNIT_TYPE_VALUE
                    ? { isCustomUnitType: true, unitType: "" }
                    : { isCustomUnitType: false, unitType: value });
                }}
                placeholder="Ünite tipi seçin"
                options={[...UNIT_TYPES, CUSTOM_UNIT_TYPE_VALUE]}
              />
            </Field>
          )}
          {usageSelected && line.isCustomUnitType && (
            <Field label="Yeni Ünite Tipi">
              <Input
                value={line.unitType}
                onChange={(event) => updateLine(line.id, { unitType: event.target.value })}
                placeholder=""
              />
            </Field>
          )}
          {usageSelected && line.unitType && (
            <Field label="Şehir">
              <CitySelect value={line.city} onChange={(city) => updateLine(line.id, { city })} />
            </Field>
          )}
          {datesVisible && <Field label="Başlangıç Tarihi"><Input type="date" value={line.startDate} onChange={(event) => updateLine(line.id, { startDate: event.target.value })} /></Field>}
          {datesVisible && <Field label="Bitiş Tarihi"><Input type="date" min={line.startDate || undefined} value={line.endDate} onChange={(event) => updateLine(line.id, { endDate: event.target.value })} /></Field>}
        </div>
        {pricingVisible && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 items-end">
            <NumberField label="Periyot" min="1" value={line.period} onChange={(period) => updateLine(line.id, { period })} />
            <NumberField label="Ünite Sayısı" min="1" value={line.unitNumber} onChange={(unitNumber) => updateLine(line.id, { unitNumber })} />
            <NumberField label="Face Sayısı" min="1" value={line.faces} onChange={(faces) => updateLine(line.id, { faces })} />
            {line.usageType === "Birim" && <NumberField label="Ünite Fiyatı (TRY)" min="0" step="0.01" value={line.unitPrice} onChange={(unitPrice) => updateLine(line.id, { unitPrice })} />}
            {usesFrequency(line) && <NumberField label="Frekans" min="1" value={line.frequency} onChange={(frequency) => updateLine(line.id, { frequency })} />}
            {line.usageType === "Paket" && <NumberField label="Paket Fiyatı (TRY)" min="0" step="0.01" value={line.packagePrice} onChange={(packagePrice) => updateLine(line.id, { packagePrice })} />}
            {line.usageType === "Free" && <div className="h-10 px-3 rounded bg-slate-50 border border-slate-200 flex items-center text-sm text-slate-600">Ücretsiz Kullanım</div>}
            <div className="h-10 px-3 rounded bg-emerald-50 border border-emerald-200 flex items-center justify-between text-sm"><span>Total Budget</span><b>{tl(lineBudget(line))}</b></div>
          </div>
        )}
        {pricingVisible && !uploadEnabled && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Lokasyon eklemek veya kaydetmek için plan/kullanım tipi, şehir, tarih, adet ve ilgili fiyat alanlarını tamamlayın.</div>}
        {uploadEnabled && (
          <div className="border-t pt-4">
            <div className="text-sm font-medium text-slate-700 mb-1">{line.unitType} Lokasyonları <span className="text-xs font-normal text-slate-400">(opsiyonel)</span></div>
            <div className="text-xs text-slate-400 mb-3">Excel daha sonra da yüklenebilir. Beklenen sütunlar: <span className="font-mono">Ünite Tipi, Adres, Latitude, Longtitude, Adet</span> veya <span className="font-mono">Ünite Tipi, Adres, Koordinat, Adet</span></div>
            <div className="flex items-center gap-3">
              <label className="h-10 px-5 rounded border-2 border-dashed border-slate-300 text-slate-600 hover:border-brand-400 text-sm inline-flex items-center cursor-pointer">Excel Seç
                <input type="file" accept=".xlsx,.xls" onChange={(event) => handleFileUpload(line.id, event)} className="hidden" />
              </label>
              {line.fileName && <span className="text-sm text-emerald-600 font-medium">{line.fileName} — {line.locations.length} lokasyon yüklendi</span>}
            </div>
          </div>
        )}
        {line.locations.length > 0 && <LocationTable locations={line.locations} onRemove={(locationId) => removeLocation(line.id, locationId)} />}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block text-sm"><div className="text-slate-600 mb-1">{label}</div>{children}</label>;
}

function Select({ value, onChange, placeholder, options }) {
  return <select value={value} onChange={onChange} className="w-full h-10 rounded border border-slate-300 bg-white px-3">
    <option value="">{placeholder}</option>{options.map((option) => <option key={option} value={option}>{option === CUSTOM_UNIT_TYPE_VALUE ? "Yeni ünite tipi ekle" : option}</option>)}
  </select>;
}

function CitySelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full h-10 rounded border border-slate-300 bg-white px-3"
    >
      <option value="">Şehir seçin</option>
      {TURKEY_CITIES.map((city) => <option key={city} value={city}>{city}</option>)}
    </select>
  );
}

function NumberField({ label, value, onChange, min, step = "1" }) {
  return <Field label={label}><Input type="number" min={min} step={step} value={value} onChange={(event) => onChange(event.target.value)} /></Field>;
}

function LocationTable({ locations, onRemove }) {
  return <div className="border rounded overflow-hidden"><div className="max-h-[360px] overflow-auto">
    <table className="min-w-[760px] w-full text-sm">
      <thead className="sticky top-0 bg-slate-50 text-left text-slate-600"><tr><th className="p-2">Ünite Tipi</th><th className="p-2">Adres</th><th className="p-2">Latitude</th><th className="p-2">Longtitude</th><th className="p-2 text-right">Adet</th><th className="p-2"></th></tr></thead>
      <tbody>{locations.map((location, index) => <tr key={location.id} className={index % 2 ? "bg-slate-50/50" : ""}>
        <td className="p-2">{location.unitType || "—"}</td><td className="p-2 max-w-[320px] truncate" title={location.address}>{location.address}</td>
        <td className="p-2">{location.latitude || "—"}</td><td className="p-2">{location.longitude || "—"}</td><td className="p-2 text-right">{location.quantity}</td>
        <td className="p-2 text-right"><button type="button" onClick={() => onRemove(location.id)} className="text-xs text-rose-600">Sil</button></td>
      </tr>)}</tbody>
    </table>
  </div></div>;
}
